import {
  Box,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
  Button,
} from "@mui/material";
import React, { useEffect } from "react";
import { useUser } from "../contexts/useUser";
import { useNavigate } from "react-router-dom";
import { API } from "aws-amplify";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchLLMCallsCountRecursive } from "../dataaccess/fetchCallCount";

const TrialAndPlan: React.FC = () => {
  const {
    project,
    entitlements,
    user,
    subscriptionActive,
    trialDaysRemaining,
  } = useUser();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [callCount, setCallCount] = React.useState(0);
  const [callCountPercentage, setCallCountPercentage] = React.useState(0);
  const [callCountFreeBudget, setCallCountFreeBudget] = React.useState(0);

  const hasLocalhost = (hostname: any) =>
    Boolean(
      hostname.match(/localhost/) ||
        hostname.match(
          /127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}/
        ) ||
        hostname.match(/dev.llmasaservice.io/)
    );

  const isDevMode = hasLocalhost(window.location.hostname);

  const getCallCountDate = () => {
    if (!project) return new Date().toISOString();

    // if there is a subscription, use that date
    if (
      project.subscription &&
      project.subscription?.subscriptionStatus === "active" &&
      project.subscription?.subscriptionStartDate
    ) {
      return project.subscription.subscriptionStartDate;
    }

    // if there is no subscription, use the project creation date
    return project.createdAt;
  };

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ["callCount"] });
  }, [project]);

  const { data, isLoading, isError } = useQuery({
    queryKey: [
      "callCount",
      { projectId: project?.id, date: getCallCountDate() },
    ],
    queryFn: () =>
      fetchLLMCallsCountRecursive(project?.id ?? "", 10000, getCallCountDate()),

    enabled:
      project &&
      project?.subscription &&
      project?.subscription.subscriptionStatus === "active"
        ? true
        : false,
  });

  isError && console.error("Error fetching call count", data);
  const callsData = data?.data?.callsByProjectIdAndCreatedAt?.items ?? [];

  useEffect(() => {
    if (!project) return;
    if (!entitlements || entitlements?.length === 0) return;

    setCallCount(callsData.length);

    if (entitlements?.includes("call-usage-tier-1")) {
      setCallCountFreeBudget(500);
      setCallCountPercentage(Math.round((callsData.length / 500) * 100));
    } else if (entitlements?.includes("call-usage-tier-2")) {
      setCallCountFreeBudget(1000);
      setCallCountPercentage(Math.round((callsData.length / 1000) * 100));
    } else if (entitlements?.includes("call-usage-tier-3")) {
      setCallCountFreeBudget(2500);
      setCallCountPercentage(Math.round((callsData.length / 2500) * 100));
    }
  }, [entitlements, callsData]);

  const maxTokens = 125000;
  const trialTokensRemaining = project?.trialTokensRemaining ?? 125000;

  // Calculate the percentage
  const percentage = (trialTokensRemaining / maxTokens) * 100;

  // Ensure the percentage is within the range 0 to 100
  const normalizedPercentage = Math.min(Math.max(percentage, 0), 100);

  const openUrlInNewPage = (url: string) => {
    window.open(url, "_blank");
  };

  const openBillingPortal = async () => {
    if (subscriptionActive === false) {
      return;
    }

    try {
      const portal = await API.post("eventapi", "/customerPortal", {
        body: {
          customerId: project?.stripeCustomerId,
          returnUrl: window.location.href,
        },
      });

      if (portal) {
        openUrlInNewPage(portal.url);
      }
    } catch (error) {
      console.error("Error getting stripe portal URL.", error as any);
    }
  };

  return (
    <>
      {!subscriptionActive ? (
        <>
          {" "}
          <Tooltip title="You are on a trial plan at the Production level feature set. Upgrade at anytime before the trial period ends to continue to develop and use. You are given 125,000 free API tokens to use during your trial period (add your own API key to LLM services if you want to keep testing)">
            <Stack spacing={1} marginBottom={2}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                width="100%"
              >
                <Box flexGrow={1} mr={2}>
                  <Typography variant="caption">
                    Trial days left: {trialDaysRemaining}
                  </Typography>
                  <LinearProgress
                    sx={{ height: "10px", borderRadius: "5px", mt: 1 }}
                    variant="determinate"
                    value={(trialDaysRemaining / 30) * 100}
                    color={trialDaysRemaining < 10 ? "warning" : "primary"}
                  />
                </Box>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => navigate("/plans")}
                  color={trialDaysRemaining < 10 ? "warning" : "primary"}
                >
                  Upgrade
                </Button>
              </Box>
              <Box>
                <>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    width="100%"
                  >
                    <Box flexGrow={1} mr={2}>
                      <>
                        <Typography variant="caption">
                          Trial Tokens:{" "}
                          {project?.trialTokensRemaining ?? 125000}
                        </Typography>
                        <LinearProgress
                          sx={{
                            height: "10px",
                            width: "100%",
                            borderRadius: "5px",
                            mt: 1,
                          }}
                          variant="determinate"
                          value={normalizedPercentage}
                        />
                      </>
                    </Box>
                  </Box>
                </>
              </Box>
            </Stack>
          </Tooltip>
        </>
      ) : (
        <>
          <Tooltip
            title={`You have an active subscription. You can download invoices, change plans or cancel using the MANAGE button. You can use your own vendor API keys, or buy credit through us to have a centralized billing experience (openAI, Anthropic and Mistral only). You have used ${callCountPercentage}% of your free call budget (${callCount} of ${callCountFreeBudget}). After this budget is used you may be charged extra for additional calls.`}
          >
            <Stack spacing={0.5} marginBottom={1}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                width="100%"
              >
                <Typography variant="caption" sx={{ mr: 3 }}>
                  {project?.subscription?.subscriptionStatus === "canceled" ? (
                    <Typography variant="caption">Plan Canceled</Typography>
                  ) : (
                    <>
                      Plan:{" "}
                      {entitlements?.includes("call-usage-tier-1")
                        ? "Developer"
                        : entitlements?.includes("call-usage-tier-2")
                        ? "Production"
                        : "Enterprise"}{" "}
                      <br />
                      {project?.subscription?.subscriptionCancelAtPeriodEnd ===
                      "true"
                        ? "cancels "
                        : "renews "}
                      {new Date(
                        project?.subscription?.subscriptionEndDate as string
                      ).toLocaleDateString()}
                    </>
                  )}
                </Typography>

                <Button
                  variant="text"
                  size="small"
                  onClick={() => openBillingPortal()}
                >
                  manage
                </Button>
              </Box>

              <Box display="flex" alignItems="center" width="100%">
                <Typography variant="caption" sx={{ mr: 2 }}>
                  Included calls
                </Typography>
                <LinearProgress
                  sx={{ height: "10px", borderRadius: "5px", flexGrow: 1 }}
                  variant="determinate"
                  value={callCountPercentage}
                />
                <Typography variant="caption" sx={{ ml: 2 }}>
                  {callCountPercentage}%
                </Typography>
              </Box>
              <hr />
              <Box>
                <>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    width="100%"
                  >
                    <Box flexGrow={1} mr={2}>
                      <>
                        <Typography variant="caption">
                          API Credit:{" "}
                          {new Intl.NumberFormat("en-US", {
                            style: "currency",
                            currency: "USD",
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 4,
                          }).format(project?.apiCreditRemaining ?? 0)}
                        </Typography>
                      </>
                    </Box>

                    <Button
                      variant="text"
                      size="small"
                      onClick={() =>
                        window.open(
                          isDevMode
                            ? `https://buy.stripe.com/test_fZe002fUEc8x2xWdQQ?client_reference_id=${project?.id}&prefilled_email=${user?.email}`
                            : `https://buy.stripe.com/4gwdSy6JB8615u8dQQ?client_reference_id=${project?.id}&prefilled_email=${user?.email}`,
                          "_blank",
                          "noopener,noreferrer"
                        )
                      }
                    >
                      top-up
                    </Button>
                  </Box>
                </>
              </Box>
            </Stack>
          </Tooltip>
        </>
      )}
    </>
  );
};

export default TrialAndPlan;
